import { ReactNode } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { TStringWithAutocomplete } from '@northladder/utilities';

import { DiceBearIcon } from '../icons';
import { LogoDealerNorthLadderShort } from '../brand';

type TSize = TStringWithAutocomplete<
  'size-6' | 'size-8' | 'size-10' | 'size-12' | 'size-16'
>;

interface IInitials {
  initials: string;
  type: 'initials';
}

interface IImage {
  src: string;
  type: 'image';
}

interface IDiceBearIcon {
  type: 'dicebear-icon';
}

interface IDiceBearAvatar {
  type: 'dicebear-avatar';
  name: string;
}

interface IIcon {
  icon: ReactNode;
  type: 'icon';
}

interface IStaff {
  type: 'staff';
}

export type TVariants =
  | IDiceBearAvatar
  | IDiceBearIcon
  | IIcon
  | IImage
  | IInitials
  | IStaff;

/**
 * Properties for the IconAvatar component.
 */
interface IconAvatarProps {
  /** The SVG or icon component to display as the avatar. */
  icon: ReactNode;
}

/**
 * -----------------------------------------------------------------------------
 * This renders the an svg to  be used as the avatar
 */
export function IconAvatar({ icon }: IconAvatarProps) {
  return (
    <div
      className="
        flex h-full w-full items-center justify-center rounded-full border
        border-green-800 bg-gradient-to-b from-green-100 to-gray-300
        p-1
      "
    >
      {icon}
    </div>
  );
}

/**
 * Properties for the InitialsAvatar component.
 */
interface IInitialsAvatarProps {
  /** The initials to display (e.g., "JD"). */
  initials: string;
  /** The size variant of the avatar. */
  size: TSize;
}

/** *
 * -----------------------------------------------------------------------------
 * This renders the initials avatar with the given initials from the user's name
 * NOTE: This is extracted out into separate components mostly for performance
 * reasons since this is going to be used in lots of places like lists.
 */
export function InitialsAvatar({
  initials,
  size = 'default',
}: IInitialsAvatarProps) {
  const textSizeClassName = size === 'default' ? 'text-base' : 'text-xs';
  return (
    <div
      className="
        h-full w-full rounded-full bg-gradient-to-b from-green-800
        to-gray-500 p-px
      "
    >
      <div
        className="
          h-full w-full rounded-full bg-gradient-to-b from-green-50
        to-gray-200
        "
      >
        <div
          className={clsx(
            'flex h-full w-full items-center justify-center bg-gradient-to-br',
            'from-green-700 to-gray-800 bg-clip-text text-sm',
            'uppercase text-green-700',
            textSizeClassName
          )}
          style={{ WebkitTextFillColor: 'transparent' }}
        >
          {initials}
        </div>
      </div>
    </div>
  );
}

/**
 * Properties for the ImageAvatar component.
 */
interface IImageAvatarProps {
  /** The source URL of the profile image. */
  src: string;
}

/** *
 * -----------------------------------------------------------------------------
 * This renders the image avatar from the profile url of the user.
 */
export function ImageAvatar({ src }: IImageAvatarProps) {
  return (
    <Image
      alt="user"
      className="h-full w-full object-cover"
      height={200}
      src={src}
      width={200}
    />
  );
}

/** *
 * -----------------------------------------------------------------------------
 * This will uses a default prebuilt avatar from dicebear that was saved as an
 * svg icon.
 */
export function DicebearIconAvatar() {
  return (
    <div className="h-full w-full rounded-full border-2 border-gray-500">
      <DiceBearIcon />
    </div>
  );
}

/**
 * Properties for the DicebearAvatar component.
 */
interface IDicebearAvatarProps {
  /** Optional CSS class name. */
  className: string;
  /** Whether the avatar image should be flipped horizontally. */
  isFlipped?: true;
  /** The seed name used to generate the unique avatar. */
  name: string;
}

/** *
 * -----------------------------------------------------------------------------
 * This will be used to render a dice bear avatar. the image avatar from the
 * profile url of the user.
 * Read MORE:::
 * https://api.dicebear.com/9.x/lorelei-gray/svg?seed=Ryan&flip=true
 */
export function DicebearAvatar({
  isFlipped = true,
  name,
  className,
}: IDicebearAvatarProps) {
  const diceBearAvatarUrl = `https://api.dicebear.com/9.x/lorelei-gray/svg?seed=${name}&flip=${isFlipped}`;

  return (
    <div
      className={clsx(
        `border-primary-950/20 hover:border-primary-500/80 size-full shrink-0 rounded-full
        border-2 bg-white transition-all duration-1000 ease-in-out
        hover:opacity-50
        `,
        className
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt={`Avatar generated for ${name}`}
        className="size-full shrink-0 rounded-full"
        src={diceBearAvatarUrl}
      />
    </div>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This will render the renexis logo avatar that is used to identify the admin
 * user/staff.
 */
function StaffAvatar() {
  return (
    <div
      className="
        h-full w-full rounded-full bg-gradient-to-b from-green-800
        to-gray-500 p-px
      "
    >
      <div
        className="
          h-full w-full rounded-full bg-gradient-to-b from-green-50
        to-gray-200
        "
      >
        <div
          className="
            flex h-full w-full items-center justify-center bg-gradient-to-br
            from-green-700 to-gray-800 text-green-700
          "
        >
          <LogoDealerNorthLadderShort className="h-full w-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Properties for the UserAvatar component.
 */
interface IUserAvatarProps {
  /** Optional click handler for the avatar. */
  onClick?: () => void;
  /** The size variant of the avatar. */
  size?: TSize;
  /** The visual variant configuration for the avatar. */
  variant: TVariants;
}

/**
 * -----------------------------------------------------------------------------
 * It renders multiple variant of avatar with icon, image and text
 */
export function UserAvatar({
  onClick,
  size,
  variant,
}: Readonly<IUserAvatarProps>) {
  let element = null;
  const { type } = variant;

  const sizeClassName = size || 'size-10';

  switch (type) {
    case 'initials': {
      element = (
        <InitialsAvatar initials={variant.initials} size={sizeClassName} />
      );
      break;
    }
    case 'image': {
      element = <ImageAvatar src={variant.src} />;
      break;
    }
    case 'dicebear-avatar': {
      element = (
        <DicebearAvatar className={sizeClassName} name={variant.name} />
      );
      break;
    }
    case 'dicebear-icon': {
      element = <DicebearIconAvatar />;
      break;
    }
    case 'icon': {
      element = <IconAvatar icon={variant.icon} />;
      break;
    }
    case 'staff': {
      element = <StaffAvatar />;
      break;
    }
    default: {
      // Dicebear prebuilt icon would be the default worst cased scenario.
      element = <DicebearIconAvatar />;
      break;
    }
  }

  if (onClick) {
    return (
      <button
        className={clsx(
          'shrink-0 overflow-hidden rounded-full border-none',
          sizeClassName
        )}
        onClick={onClick}
        type="button"
      >
        {element}
      </button>
    );
  }

  return (
    <div
      className={clsx('shrink-0 overflow-hidden rounded-full', sizeClassName)}
    >
      {element}
    </div>
  );
}
